/**
 * Ultrasound Sound Files Hook
 * 
 * Custom hook for managing ultrasound sound files data including loading,
 * error handling, and automatic data fetching on parameter changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { UltsSoundFiles, UltrasoundError, DateRange } from '../services/equipment/types';
import { ultrasoundService } from '../services/equipment/ultrasound-service';
import { useDateRange } from './use-date-range';

interface UseUltsSoundFilesOptions {
  /** Site identifier */
  site?: string;
  /** Asset identifier */
  asset?: string;
  /** Position identifier (may include equipment prefix) */
  position?: string;
}

interface UseUltsSoundFilesReturn {
  /** Baseline data */
  data: UltsSoundFiles | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Ultrasound-specific error object for detailed error handling */
  ultrasoundError: UltrasoundError | null;
  /** Fetch baseline data function */
  fetchSoundFiles: (site: string, asset: string, position: string, targetDateRange: DateRange) => Promise<void>;
  /** Retry the last failed request */
  retry: () => Promise<void>;
  /** Clear data and reset state */
  clear: () => void;
}

/**
 * Hook for managing ultrasound baseline data
 */
export const useUltsSoundFiles = (
  options: UseUltsSoundFilesOptions
): UseUltsSoundFilesReturn => {
  const {
    site,
    asset,
    position
  } = options;

  const { dateRange: contextDateRange } = useDateRange();

  const [data, setData] = useState<UltsSoundFiles | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ultrasoundError, setUltrasoundError] = useState<UltrasoundError | null>(null);
  const [currentSite, setCurrentSite] = useState<string | null>(null);
  const [currentAsset, setCurrentAsset] = useState<string | null>(null);
  const [currentPosition, setCurrentPosition] = useState<string | null>(null);
  const [currentDateRange, setCurrentDateRange] = useState<DateRange | null>(contextDateRange);

  /**
   * Fetch ultrasound baseline data
   */
  const fetchSoundFiles = useCallback(async (
    targetSite: string,
    targetAsset: string,
    targetPosition: string,
    targetDateRange: DateRange
  ) => {
    if (!targetSite || !targetAsset || !targetPosition) return;

    setIsLoading(true);
    setError(null);
    setUltrasoundError(null);

    try {
      const soundFilesData = await ultrasoundService.getSoundFiles(
        targetSite,
        targetAsset,
        targetPosition,
        targetDateRange.startDate,
        targetDateRange.endDate
      );
      console.log(`[use-ults-sound-files.tsx] files data: ${soundFilesData}`);
      setData(soundFilesData);
      setCurrentSite(targetSite);
      setCurrentAsset(targetAsset);
      setCurrentPosition(targetPosition);
      setCurrentDateRange(targetDateRange);
    } catch (err) {
      if (err instanceof UltrasoundError) {
        setUltrasoundError(err);
        setError(err.message);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ultrasound files data';
        setError(errorMessage);
        setUltrasoundError(null);
      }
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Retry the last failed request
   */
  const retry = useCallback(async () => {
    if (!currentSite || !currentAsset || !currentPosition || !currentDateRange) return;
    await fetchSoundFiles(currentSite, currentAsset, currentPosition, currentDateRange);
  }, [currentSite, currentAsset, currentPosition, currentDateRange, fetchSoundFiles]);

  /**
   * Clear data and reset state
   */
  const clear = useCallback(() => {
    setData(null);
    setError(null);
    setUltrasoundError(null);
    setCurrentSite(null);
    setCurrentAsset(null);
    setCurrentPosition(null);
    setCurrentDateRange(null);
  }, []);

  // Auto-fetch when parameters change - always fetch when valid parameters are provided
  useEffect(() => {
    // Always fetch data when all parameters are provided and any parameter has changed
    if (site && asset && position && contextDateRange) {
      if (site !== currentSite || asset !== currentAsset || position !== currentPosition || contextDateRange != currentDateRange) {
        fetchSoundFiles(site, asset, position, contextDateRange);
      }
    }
  }, [site, asset, position, contextDateRange, currentSite, currentAsset, currentPosition, currentDateRange, fetchSoundFiles]);

  // Clear data when parameters are removed
  useEffect(() => {
    if (
      (!site && currentSite) ||
      (!asset && currentAsset) ||
      (!position && currentPosition) ||
      (!contextDateRange && currentDateRange)
    ) {
      clear();
    }
  }, [site, currentSite, asset, currentAsset, position, currentPosition, contextDateRange, currentDateRange, clear]);

  return {
    data,
    isLoading,
    error,
    ultrasoundError,
    fetchSoundFiles,
    retry,
    clear
  };
};